import React, { useMemo, useState, useEffect } from "react";
import { Image, Platform, Pressable, ScrollView, ActivityIndicator } from "react-native";
import { FontAwesome, FontAwesome6, AntDesign } from '@expo/vector-icons';
import { themeSelector } from "@reduxLocal/selectors/themeSelector";
import InputFieldStyled from "@components/Global/InputFieldStyled";
import handleImageUpload from "@utils/handleImage";
import apis from "@api/api";
import { handleGetLocation } from "@hooks/handleGetLocation";
import useGlobalUser from "@hooks/getGlobalUser";
import { useDispatch, useSelector } from "react-redux";
import { DefaultRootState } from "@reduxLocal/persistState";
import { Popover, ZStack, View, Text, XStack, YStack, Dialog } from "tamagui";
import { Picker } from 'emoji-mart-native';
import * as Animatable from 'react-native-animatable';
import { Filter } from 'bad-words';
import { selectUser } from "@reduxLocal/selectors/userSelectors";
import { postShare } from "@api/linkedInApi";
import ImageHandlerNSFWButton from "@utils/ImageHandlerNSFWButton";
import { useNavigation } from "@react-navigation/native";
import { useQueryClient } from "@tanstack/react-query";
import { uniqueId } from "lodash";
import { scheduleNotification } from "@utils/notifications/noticeScheduler";
import { CheckBox } from "@constants/Themed";
import { useTranslation } from "react-i18next";
import { useIsFreePlanWithContext } from "@utils/billingUtils";
import { checkPostRateLimit, storeLastPostTimestamp, formatRateLimitMessage } from "@utils/postRateLimiter";
import { HOST } from '@api/constant';

// Preload the scheduler component
const SocialMediaSchedulerLazy = React.lazy(() => import('./SocialMediaScheduler'));

// Preload component when module loads
let schedulerPreloaded = false;
const preloadScheduler = () => {
  if (!schedulerPreloaded) {
    schedulerPreloaded = true;
    import('./SocialMediaScheduler').catch(err => {
      console.error('Failed to preload SocialMediaScheduler:', err);
      schedulerPreloaded = false;
    });
  }
};

// Preload immediately
preloadScheduler();

const allowedList = ['linkedin', 'tiktok', 'instagram', 'x', 'facebook', 'whatsapp'];
interface PostToFeedProps {
  refetch?: () => void;
  appendToFeed?: (data: any) => void;
}

const PostToFeed = ({ refetch, appendToFeed, selectedIntegrations, setSelectedIntegrations }) => {
  const [messageValue, setMessageValue] = useState('');
  const [postImage, setPostImage] = useState({ uri: null });
  const [postURL, setPostURL] = useState(null);
  const [showSchedulerModal, setShowSchedulerModal] = useState(false);
  const [selectedDataForm, setSelectedDataForm] = useState(null);
  const [userDataForms, setUserDataForms] = useState([]);
  const [showDataFormSelector, setShowDataFormSelector] = useState(false);
  const [dataFormPopoverOpen, setDataFormPopoverOpen] = useState(false);
  const [emojiPopoverOpen, setEmojiPopoverOpen] = useState(false);
  const [socialMediaPopoverOpen, setSocialMediaPopoverOpen] = useState(false);
  const [schedulePopoverOpen, setSchedulePopoverOpen] = useState(false);
  const theme = themeSelector();
  const filter = new Filter();
  const dispatch = useDispatch();
  const selector = useSelector((state: DefaultRootState) => state.app.location);
  const user = selectUser();
  const selectedId = useGlobalUser();
  const userIntegrations = user.authentications;
  const [textFieldFocused, setTextFieldFocused] = useState(false);
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const isFreePlan = useIsFreePlanWithContext();

  // Fetch user's data forms when popover opens
  useEffect(() => {
    const fetchUserDataForms = async () => {
      try {
        if (selectedId && dataFormPopoverOpen) {
          console.log('📋 Fetching data forms for user:', selectedId);
          const forms = await apis.getDataFolders({ userId: selectedId, dataFilter: 'all' });
          console.log('📋 Fetched data forms:', forms?.length || 0, 'forms');
          setUserDataForms(forms || []);
        }
      } catch (error) {
        console.error('❌ Error fetching user data forms:', error);
        setUserDataForms([]);
      }
    };
    fetchUserDataForms();
  }, [selectedId, dataFormPopoverOpen]);

  // Preload scheduler when modal opens
  useEffect(() => {
    if (showSchedulerModal) {
      preloadScheduler();
    }
  }, [showSchedulerModal]);

  const getContent = () => {
    try {
      return filter.clean(messageValue);
    } catch (e) {
      return messageValue;
    }
  }
  const _handleScheduledPost = React.useCallback(async () => {
    // Check rate limit before scheduling
    const rateLimitResult = await checkPostRateLimit(isFreePlan);
    if (!rateLimitResult.allowed) {
      const errorMessage = formatRateLimitMessage(rateLimitResult, isFreePlan);
      alert(errorMessage);
      return;
    }

    const location = await handleGetLocation(dispatch, selector);
    const cleanImage = { ...postImage, uri: null };

    // Check if there are other integrations selected (besides just the feed)
    const hasOtherIntegrations = Object.keys(selectedIntegrations).some(
      key => selectedIntegrations[key] && key !== 'feed'
    );

    // If model is selected and other integrations are available, include form URL (without userId and iframe for external platforms)
    let finalUrl = postURL;
    if (selectedDataForm?._id && hasOtherIntegrations) {
      const formUrl = `${HOST}/forms/builder?id=${selectedDataForm._id}`;
      // Use form URL if no other URL is provided, otherwise keep existing URL
      finalUrl = postURL || formUrl;
    }

    const scheduleData = {
      content: getContent(),
      url: finalUrl,
      integrations: selectedIntegrations,
      image: cleanImage,
      location: location.coords,
      scheduledTime: new Date(Date.now() + 60 * 1000).toISOString(),
      id: uniqueId('scheduled-post-'),
      data_link: selectedDataForm?._id || null
    };

    console.log('📅 Scheduling post with data_link:', scheduleData.data_link, 'from model:', selectedDataForm?.dataTitle);
    if (hasOtherIntegrations && selectedDataForm?._id) {
      console.log('🔗 Including form URL for social integrations:', finalUrl);
    }

    await apis.scheduleToFeed(scheduleData);
    
    // Store last post timestamp after successful scheduling
    await storeLastPostTimestamp();

    // Clear form
    setMessageValue('');
    setPostImage({ uri: null });
    setPostURL(null);
    setSelectedDataForm(null);

    if (Platform.OS != 'web') {
      await scheduleNotification({
        id: uniqueId('scheduled-post-'),
        title: "Your scheduled post is ready to go!",
        body: "Your post will be published in 5 minutes.",
        date: new Date(Date.now() + 60 * 1000),
        dependencies: {
          content: getContent(),
          url: postURL,
          image: cleanImage,
          location: location.coords,
          id: uniqueId('scheduled-post-')
        }
      })
    }

  }, [dispatch, selectedIntegrations, selector, postImage, postURL, getContent, isFreePlan, selectedDataForm]);

  const optionActions = useMemo(() => [
    {
      id: 'schedule',
      label: 'Schedule Post',
      icon: <FontAwesome name="calendar" size={24} color="rgb(90,192,234)" />,
      onPress: () => {
        setSchedulePopoverOpen(false);
        _handleScheduledPost();
      }
    },
    {
      id: "scheduleAI",
      label: "Schedule with AI",
      icon: <FontAwesome6 name="robot" size={24} color="rgb(90,192,234)" />,
      onPress: () => {
        setSchedulePopoverOpen(false);
        setShowSchedulerModal(true);
      }
    }
  ], [_handleScheduledPost]);

  const _handlePost = async () => {
    // Check rate limit before posting
    const rateLimitResult = await checkPostRateLimit(isFreePlan);
    if (!rateLimitResult.allowed) {
      const errorMessage = formatRateLimitMessage(rateLimitResult, isFreePlan);
      alert(errorMessage);
      return;
    }

    // console.log('Posting message: ', filter.clean(messageValue));
    const location = await handleGetLocation(dispatch, selector);
    const cleanImage = { ...postImage, uri: null };

    // --- DEPRECATED ---  THIS SHOULD BE HANDLED IN THE BACKEND --- LEAVING AS A REMINDER FOR MY STUPIDITY ---
    // try {
    //If linkedIn is in the integrations, post to linkedIn
    // if (userIntegrations.linkedin) {
    //   await postShare("",{ content: getContent(), url: postURL, image: cleanImage, location: location.coords }).then(res => {
    //     refetch?.();
    //     if (res) {
    //       setMessageValue('');
    //       setPostImage({ uri: null });
    //     }
    //   });
    //   }
    // } catch (e) {
    //   console.warn("Social media integration failed", e);
    //   console.warn("Social media integration failed", e);
    // }

    // Check if there are other integrations selected (besides just the feed)
    const hasOtherIntegrations = Object.keys(selectedIntegrations).some(
      key => selectedIntegrations[key] && key !== 'feed'
    );

    // If model is selected and other integrations are available, include form URL (without userId and iframe for external platforms)
    let finalUrl = postURL;
    if (selectedDataForm?._id && hasOtherIntegrations) {
      const formUrl = `${HOST}/forms/builder?id=${selectedDataForm._id}`;
      // Use form URL if no other URL is provided, otherwise keep existing URL
      finalUrl = postURL || formUrl;
    }

    const postData = {
      content: getContent(),
      integrations: selectedIntegrations,
      url: finalUrl,
      image: cleanImage,
      location: location.coords,
      data_link: selectedDataForm?._id || null
    };

    console.log('📤 Posting to feed with data_link:', postData.data_link, 'from model:', selectedDataForm?.dataTitle);
    if (hasOtherIntegrations && selectedDataForm?._id) {
      console.log('🔗 Including form URL for social integrations:', finalUrl);
    }

    await apis.postToFeed(postData).then(async (res) => {
      // Store last post timestamp after successful post
      await storeLastPostTimestamp();
      
      queryClient.setQueryData(['businessFeed'], (oldData: any[] = []) => [...oldData, res.data]);
      // Clear form
      setMessageValue('');
      setPostImage({ uri: null });
      setPostURL(null);
      setSelectedDataForm(null);
    });
  };

  return (
    <Animatable.View transition={'marginTop'} style={{
      flexDirection: "row",
      gap: 16,
      marginTop: textFieldFocused ? 60 : 16,
      overflow: "visible",
      paddingHorizontal: 4,
    }}>
      {postImage?.uri && (
        <Animatable.View useNativeDriver style={{
          flex: 1,
          width: 150,
          left: -10,
          top: -5,
          borderRadius: 12,
          overflow: 'hidden',
          elevation: 3,
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 2 },
          shadowOpacity: 0.1,
          shadowRadius: 4,
        }} animation={'slideInLeft'}>
          <Pressable
            style={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 10,
              backgroundColor: 'rgba(0,0,0,0.6)',
              borderRadius: 20,
              width: 32,
              height: 32,
              alignItems: 'center',
              justifyContent: 'center'
            }}
            onPress={() => setPostImage({ uri: null })}
          >
            <FontAwesome name="times" size={16} color="white" />
          </Pressable>
          <Image
            source={{ uri: postImage?.uri }}
            style={{
              width: 90,
              height: 90,
              borderRadius: 12,
              margin: 12,
              borderWidth: 2,
              borderColor: theme ? "rgba(90,192,234,0.3)" : "rgba(90,192,234,0.5)",
            }}
          />
        </Animatable.View>
      )}
      {selectedDataForm && (
        <Animatable.View useNativeDriver style={{
          flex: 1,
          width: 150,
          left: postImage?.uri ? 140 : -10,
          top: -5,
          borderRadius: 12,
          overflow: 'hidden',
          elevation: 3,
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 2 },
          shadowOpacity: 0.1,
          shadowRadius: 4,
          backgroundColor: theme ? 'rgba(90,192,234,0.1)' : 'rgba(90,192,234,0.2)',
        }} animation={'slideInLeft'}>
          <Pressable
            style={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 10,
              backgroundColor: 'rgba(0,0,0,0.6)',
              borderRadius: 20,
              width: 32,
              height: 32,
              alignItems: 'center',
              justifyContent: 'center'
            }}
            onPress={() => setSelectedDataForm(null)}
          >
            <FontAwesome name="times" size={16} color="white" />
          </Pressable>
          <View style={{
            width: 90,
            height: 90,
            borderRadius: 12,
            margin: 12,
            borderWidth: 2,
            borderColor: theme ? "rgba(90,192,234,0.3)" : "rgba(90,192,234,0.5)",
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: theme ? '#fff' : '#2a2a2a',
          }}>
            <FontAwesome name="wpforms" size={32} color="rgb(90,192,234)" />
            <Text style={{
              fontSize: 10,
              color: theme ? '#333' : '#fff',
              marginTop: 4,
              textAlign: 'center',
              paddingHorizontal: 4
            }} numberOfLines={2}>
              {selectedDataForm.dataTitle}
            </Text>
          </View>
        </Animatable.View>
      )}
      <View style={{
        flex: 4,
        justifyContent: 'flex-start',
        alignItems: 'center',
        backgroundColor: 'transparent',
        alignSelf: 'stretch',
        maxWidth: "100%",
        borderRadius: 16,
        paddingHorizontal: 2,
      }}>
        <InputFieldStyled
          maxLength={150}
          minHeight={textFieldFocused ? 100 : 50}
          onFocus={() => {
            setTextFieldFocused(true);
          }}
          onBlur={() => {
            setTextFieldFocused(false);
          }}
          multiline
          style={{
            borderLeftWidth: 0,
            top: -10,
            borderRightWidth: 0,
            borderTopWidth: 0,
            color: theme ? 'black' : 'white',
            backgroundColor: 'transparent',
            borderBottomWidth: 2,
            borderBottomColor: theme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)',
            paddingHorizontal: 12,
            paddingVertical: 8,
          }}
          placeholderTextColor={theme ? '#666' : '#999'}
          textContentType="none"
          value={messageValue}
          placeholder={t("What would you like to post?")}
          onChangeText={setMessageValue}
        />
        <View style={{
          backgroundColor: 'transparent',
          flexDirection: 'row',
          flex: 1,
          alignSelf: 'stretch',
          justifyContent: "space-between",
          minHeight: 50,
          height: 55, minWidth: "110%",
          paddingHorizontal: 4,
          alignItems: 'center',
          gap: 12,
        }}>
          {/* Action Buttons Container - Pill Design */}
          <View style={{
            flexDirection: "row",
            backgroundColor: theme ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.08)',
            borderRadius: 28,
            paddingHorizontal: 4,
            paddingVertical: 4,
            gap: 2,
            borderWidth: 1,
            paddingRight: 40,
            borderColor: theme ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.06)',
          }}>
            <Pressable
              style={({ pressed }) => ({
                padding: 10,
                borderRadius: 24,
                backgroundColor: pressed
                  ? (theme ? 'rgba(90,192,234,0.2)' : 'rgba(90,192,234,0.3)')
                  : 'transparent',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: 44,
                left: 10,
                minHeight: 44,
              })}
              onPress={() => handleImageUpload(true).then(image => {
                if (typeof image === 'object' && image.uri) {
                  setPostImage(image);
                }
              })}
            >
              <FontAwesome
                name="image"
                size={20}
                color={theme ? "#333" : "#fff"}
              />
            </Pressable>
            <Pressable
              style={({ pressed }) => ({
                padding: 10,
                borderRadius: 24,
                backgroundColor: pressed
                  ? (theme ? 'rgba(90,192,234,0.2)' : 'rgba(90,192,234,0.3)')
                  : 'transparent',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: 44,
                minHeight: 44,
              })}
              onPress={() => {
                if (Platform.OS === 'web') {
                  const url = prompt('Enter the URL of the video you would like to include in your post (YouTube, Vimeo, etc.)');
                  if (url) {
                    setPostURL(url);
                  }
                } else {
                  alert('This feature is only available on the web version of the app');
                }
              }}
            >
              <FontAwesome
                name="video-camera"
                size={20}
                left={-5}
                color={theme ? "#333" : "#fff"}
              />
            </Pressable>
            {/* Data Form Link Button */}
            <Popover
              open={dataFormPopoverOpen}
              onOpenChange={setDataFormPopoverOpen}
              stayInFrame
              allowFlip
              style={{ minWidth: 40 }}
              offset={{
                mainAxis: 16,
                crossAxis: 0,
              }}
            >
              <Popover.Content
                borderRadius={16}
                enterStyle={{ y: -10, opacity: 0, scale: 0.9 }}
                exitStyle={{ y: -10, opacity: 0, scale: 0.9 }}
                elevate
                backgroundColor={theme ? "#fff" : "#2a2a2a"}
                borderWidth={1}
                borderColor={theme ? "rgba(0,0,0,0.1)" : "rgba(255,255,255,0.1)"}
                shadowColor="#000"
                shadowOffset={{ width: 0, height: 8 }}
                shadowOpacity={0.15}
                shadowRadius={24}
                padding={16}
                animation={[
                  'quick',
                  {
                    opacity: {
                      overshootClamping: true,
                    },
                    scale: {
                      overshootClamping: true,
                    },
                  },
                ]}
              >
                <View style={{ maxHeight: 300, minWidth: 250 }}>
                  <Text style={{
                    fontSize: 16,
                    fontWeight: '600',
                    color: theme ? '#333' : '#fff',
                    marginBottom: 12
                  }}>
                    Select a Model to Share
                  </Text>
                  {userDataForms.length === 0 ? (
                    <Text style={{
                      fontSize: 14,
                      color: theme ? '#666' : '#999',
                      textAlign: 'center',
                      padding: 20
                    }}>
                      No models available
                    </Text>
                  ) : (
                      <ScrollView style={{ gap: 8 }}>
                      {userDataForms.map((form, index) => (
                        <Pressable
                          key={form._id || index}
                          onPress={() => {
                            setDataFormPopoverOpen(false);
                            console.log('✅ Selected model:', form.dataTitle, 'with ID:', form._id);
                            setSelectedDataForm(form);
                          }}
                          style={({ pressed }) => ({
                            flexDirection: 'row',
                            alignItems: 'center',
                            paddingVertical: 12,
                            paddingHorizontal: 12,
                            borderRadius: 12,
                            backgroundColor: selectedDataForm?._id === form._id
                              ? (theme ? 'rgba(90,192,234,0.2)' : 'rgba(90,192,234,0.3)')
                              : pressed
                                ? (theme ? 'rgba(90,192,234,0.1)' : 'rgba(90,192,234,0.2)')
                                : 'transparent',
                          })}
                        >
                          <FontAwesome
                            name={selectedDataForm?._id === form._id ? "check-circle" : "circle-o"}
                            size={20}
                            color={selectedDataForm?._id === form._id ? "rgb(90,192,234)" : (theme ? "#666" : "#999")}
                            style={{ marginRight: 12 }}
                          />
                          <Text style={{
                            flex: 1,
                            fontSize: 15,
                            fontWeight: '500',
                            color: theme ? '#333' : '#fff'
                          }}>
                            {form.dataTitle || 'Untitled Model'}
                          </Text>
                        </Pressable>
                      ))}
                      </ScrollView>
                  )}
                </View>
              </Popover.Content>
              <Popover.Trigger asChild>
                <Pressable
                  style={({ pressed }) => ({
                    padding: 10,
                    borderRadius: 24,
                    backgroundColor: selectedDataForm
                      ? 'rgba(90,192,234,0.3)'
                      : pressed
                        ? (theme ? 'rgba(90,192,234,0.2)' : 'rgba(90,192,234,0.3)')
                        : 'transparent',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: 44,
                    minHeight: 44,
                  })}
                >
                  <FontAwesome
                    name="wpforms"
                    size={20}
                    style={{ top: 12, textAlign: 'center' }}
                    color={selectedDataForm ? "rgb(90,192,234)" : (theme ? "#333" : "#fff")}
                  />
                </Pressable>
              </Popover.Trigger>
            </Popover>
            <Popover
              open={emojiPopoverOpen}
              onOpenChange={setEmojiPopoverOpen}
              stayInFrame
              allowFlip
              offset={{
                mainAxis: 16,
                crossAxis: 0,
              }}
            >
              <Popover.Content
                borderRadius={16}
                enterStyle={{ y: -10, opacity: 0, scale: 0.9 }}
                exitStyle={{ y: -10, opacity: 0, scale: 0.9 }}
                elevate
                backgroundColor={theme ? "#fff" : "#2a2a2a"}
                borderWidth={1}
                borderColor={theme ? "rgba(0,0,0,0.1)" : "rgba(255,255,255,0.1)"}
                shadowColor="#000"
                shadowOffset={{ width: 0, height: 8 }}
                shadowOpacity={0.15}
                shadowRadius={24}
                padding={0}
                animation={[
                  'quick',
                  {
                    opacity: {
                      overshootClamping: true,
                    },
                    scale: {
                      overshootClamping: true,
                    },
                  },
                ]}
              >
                <View style={{ borderRadius: 16, overflow: 'hidden' }}>
                  <Picker
                    onSelect={(e) => {
                      setEmojiPopoverOpen(false);
                      setMessageValue(value => value + e.native);
                    }}
                    theme={theme ? 'light' : 'dark'}
                  />
                </View>
              </Popover.Content>
              <Popover.Trigger asChild>
                <Pressable
                  style={({ pressed }) => ({
                    padding: 10,
                    borderRadius: 24,
                    left: 10,
                    backgroundColor: pressed
                      ? (theme ? 'rgba(90,192,234,0.2)' : 'rgba(90,192,234,0.3)')
                      : 'transparent',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: 44,
                    minHeight: 44,
                  })}
                >
                  <FontAwesome
                    name="smile-o"
                    size={20}
                    style={{ left: 12, top: 12 }}
                    color={theme ? "#333" : "#fff"}
                  />
                </Pressable>
              </Popover.Trigger>
            </Popover>
          </View>

          {/* Social Media Integration Selector */}
          <View style={{
            flex: 1,
            alignItems: 'center',
            justifyContent: 'center',
            paddingHorizontal: 8,
          }}>
            <Popover
              open={socialMediaPopoverOpen}
              onOpenChange={setSocialMediaPopoverOpen}
              placement="bottom"
              stayInFrame
            >
              <Popover.Trigger asChild>
                <Pressable
                  style={({ pressed }) => ({
                    flexDirection: 'row',
                    alignItems: 'center',
                    paddingHorizontal: 12,
                    paddingVertical: 8,
                    borderRadius: 20,
                    backgroundColor: pressed ? (theme ? 'rgba(90,192,234,0.1)' : 'rgba(90,192,234,0.2)') : 'transparent',
                    borderWidth: 1,
                    borderColor: theme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)',
                    minHeight: 40,
                    minWidth: 120,
                  })}
                >
                  <XStack gap={6} alignItems="center" justifyContent="center" flex={1}>
                    {Object.keys(userIntegrations).length > 0 && Object.keys(userIntegrations).slice(0, 3).map((key, index) => {
                      if (!allowedList.includes(key)) return null;
                      if (key === 'Mail' || key === 'mail') return null;

                      const getIconName = (key: string) => {
                        if (key === 'x' || key === 'X') return 'twitter';
                        if (key === 'linkedin') return 'linkedin';
                        if (key === 'facebook') return 'facebook';
                        if (key === 'instagram') return 'instagram';
                        if (key === 'tiktok') return 'music';
                        if (key === 'whatsapp') return 'whatsapp';
                        return 'share';
                      };

                      return (
                        <View
                          key={index}
                          style={{
                            width: 18,
                            height: 18,
                            borderRadius: 9,
                            backgroundColor: selectedIntegrations[key]
                              ? 'rgba(90,192,234,0.3)'
                              : 'transparent',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderWidth: selectedIntegrations[key] ? 1 : 0,
                            borderColor: 'rgb(90,192,234)',
                          }}
                        >
                          <FontAwesome 
                            name={getIconName(key) as any}
                            size={24} 
                            color={selectedIntegrations[key] ? "rgb(90,192,234)" : (theme ? "#666" : "#999")}
                          />
                        </View>
                      );
                    })}
                    <FontAwesome
                      name="chevron-down"
                      size={10}
                      color={theme ? "#666" : "#999"}
                      style={{ marginLeft: 2 }}
                    />
                  </XStack>
                </Pressable>
              </Popover.Trigger>
              <Popover.Content
                borderRadius={16}
                backgroundColor={theme ? "#fff" : "#2a2a2a"}
                borderWidth={1}
                borderColor={theme ? "rgba(0,0,0,0.1)" : "rgba(255,255,255,0.1)"}
                shadowColor="#000"
                shadowOffset={{ width: 0, height: 8 }}
                shadowOpacity={0.15}
                shadowRadius={24}
                padding={16}
                minWidth={200}
                enterStyle={{ y: -10, opacity: 0, scale: 0.95 }}
                exitStyle={{ y: -10, opacity: 0, scale: 0.95 }}
                animation={[
                  'quick',
                  {
                    opacity: { overshootClamping: true },
                    scale: { overshootClamping: true },
                  },
                ]}
              >
                <Popover.Arrow
                  borderWidth={1}
                  borderColor={theme ? "rgba(0,0,0,0.1)" : "rgba(255,255,255,0.1)"}
                  backgroundColor={theme ? "#fff" : "#2a2a2a"}
                />
                <YStack gap={12}>
                  <Text style={{
                    fontSize: 16,
                    fontWeight: '600',
                    color: theme ? '#333' : '#fff',
                    marginBottom: 8
                  }}>
                    Post to Platforms
                  </Text>
                  {Object.keys(userIntegrations).length > 0 && Object.keys(userIntegrations).map((key, index) => {
                    if (!allowedList.includes(key)) return null;
                    if (key === 'Mail' || key === 'mail') return null;

                    const getIconName = (key: string) => {
                      if (key === 'x' || key === 'X') return 'twitter';
                      if (key === 'linkedin') return 'linkedin';
                      if (key === 'facebook') return 'facebook';
                      if (key === 'instagram') return 'instagram';
                      if (key === 'tiktok') return 'music';
                      if (key === 'whatsapp') return 'whatsapp';
                      return 'share';
                    };

                    const getPlatformName = (key: string) => {
                      if (key === 'x' || key === 'X') return 'X (Twitter)';
                      return key.charAt(0).toUpperCase() + key.slice(1);
                    };

                    return (
                      <Pressable
                        key={index}
                        onPress={() => {
                          setSocialMediaPopoverOpen(false);
                          setSelectedIntegrations(prev => ({
                            ...prev,
                            [key]: !prev[key]
                          }));
                        }}
                        style={({ pressed }) => ({
                          flexDirection: 'row',
                          alignItems: 'center',
                          paddingVertical: 12,
                          paddingHorizontal: 12,
                          borderRadius: 12,
                          backgroundColor: pressed
                            ? (theme ? 'rgba(90,192,234,0.1)' : 'rgba(90,192,234,0.2)')
                            : 'transparent',
                        })}
                      >
                        <CheckBox
                          value={selectedIntegrations[key] ? true : false}
                          onValueChange={() => {
                            setSocialMediaPopoverOpen(false);
                            setSelectedIntegrations(prev => ({
                              ...prev,
                              [key]: !prev[key]
                            }));
                          }}
                          style={{ marginRight: 12 }}
                        />
                        <View style={{
                          width: 32,
                          height: 32,
                          borderRadius: 16,
                          backgroundColor: selectedIntegrations[key]
                            ? 'rgba(90,192,234,0.2)'
                            : (theme ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.05)'),
                          alignItems: 'center',
                          justifyContent: 'center',
                          marginRight: 12,
                        }}>
                          <FontAwesome
                            name={getIconName(key) as any}
                            size={18}
                            color={selectedIntegrations[key] ? "rgb(90,192,234)" : (theme ? "#666" : "#999")} 
                          />
                        </View>
                        <Text style={{
                          flex: 1,
                          fontSize: 15,
                          fontWeight: '500',
                          color: theme ? '#333' : '#fff'
                        }}>
                          {getPlatformName(key)}
                        </Text>
                      </Pressable>
                    );
                  })}
                </YStack>
              </Popover.Content>
            </Popover>
          </View>

          {/* Send Button Section */}
          <View
            style={{
              alignItems: 'center',
              justifyContent: 'flex-end',
              flexDirection: 'row',
              gap: 8,
              paddingLeft: 8,
            }}
          >
            <Pressable
              style={({ pressed }) => ({
                flexDirection: 'row',
                alignItems: 'center',
                paddingHorizontal: 16,
                paddingVertical: 10,
                borderRadius: 24,
                backgroundColor: pressed
                  ? 'rgba(90,192,234,0.9)'
                  : 'rgb(90,192,234)',
                shadowColor: '#000',
                shadowOffset: { width: 0, height: 2 },
                shadowOpacity: 0.1,
                shadowRadius: 4,
                elevation: 3,
              })}
              onPress={() => {
                if (messageValue.trim().length === 0) {
                  alert('Try adding some text, or an image to post!');
                } else {
                  _handlePost();
                }
              }}
            >
              <FontAwesome
                name="send"
                size={16}
                color="white"
                style={{ marginRight: 8 }}
              />
              <Text style={{
                color: 'white',
                fontSize: 14,
                fontWeight: '600'
              }}>
                Post
              </Text>
            </Pressable>

            <Popover
              open={schedulePopoverOpen}
              onOpenChange={setSchedulePopoverOpen}
              stayInFrame
              placement="bottom"
            >
              <Popover.Trigger asChild>
                <Pressable
                  style={({ pressed }) => ({
                    padding: 10,
                    borderRadius: 20,
                    backgroundColor: pressed
                      ? (theme ? 'rgba(90,192,234,0.1)' : 'rgba(90,192,234,0.2)')
                      : 'transparent',
                    borderWidth: 1,
                    borderColor: theme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)',
                    alignItems: 'center',
                    justifyContent: 'center',
                  })}
                >
                  <FontAwesome
                    name="chevron-down"
                    size={14}
                    color={theme ? "#666" : "#999"}
                  />
                </Pressable>
              </Popover.Trigger>
              <Popover.Content
                borderRadius={16}
                backgroundColor={theme ? "#fff" : "#2a2a2a"}
                borderWidth={1}
                borderColor={theme ? "rgba(0,0,0,0.1)" : "rgba(255,255,255,0.1)"}
                shadowColor="#000"
                shadowOffset={{ width: 0, height: 8 }}
                shadowOpacity={0.15}
                shadowRadius={24}
                padding={8}
                minWidth={240}
                enterStyle={{ y: -10, opacity: 0, scale: 0.95 }}
                exitStyle={{ y: -10, opacity: 0, scale: 0.95 }}
                animation={[
                  'quick',
                  {
                    opacity: { overshootClamping: true },
                    scale: { overshootClamping: true },
                  },
                ]}
              >
                <Popover.Arrow 
                  borderWidth={1} 
                  borderColor={theme ? "rgba(0,0,0,0.1)" : "rgba(255,255,255,0.1)"}
                  backgroundColor={theme ? "#fff" : "#2a2a2a"}
                />
                <YStack gap={4}>
                  {optionActions.map((action) => (
                    <Pressable
                      key={action.id}
                      onPress={action.onPress}
                      style={({ pressed }) => ({
                        flexDirection: 'row',
                        alignItems: 'center',
                        paddingVertical: 12,
                        paddingHorizontal: 16,
                        borderRadius: 12,
                        backgroundColor: pressed
                          ? (theme ? 'rgba(90,192,234,0.1)' : 'rgba(90,192,234,0.2)')
                          : 'transparent',
                      })}
                    >
                      <View style={{
                        width: 40,
                        height: 40,
                        borderRadius: 20,
                        backgroundColor: theme ? 'rgba(90,192,234,0.1)' : 'rgba(90,192,234,0.2)',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginRight: 12,
                      }}>
                        {action.icon}
                      </View>
                      <YStack flex={1}>
                        <Text style={{
                          fontSize: 15,
                          fontWeight: '600',
                          color: theme ? '#333' : '#fff',
                          marginBottom: 2,
                        }}>
                          {action.label}
                        </Text>
                        <Text style={{
                          fontSize: 12,
                          color: theme ? '#666' : '#999',
                        }}>
                          {action.id === 'schedule'
                            ? 'Schedule your post for later'
                            : 'Let AI optimize your posting time'
                          }
                        </Text>
                      </YStack>
                    </Pressable>
                  ))}
                </YStack>
              </Popover.Content>
            </Popover>
          </View>
        </View>
      </View>

      {/* Social Media Scheduler Modal */}
      <Dialog open={showSchedulerModal} onOpenChange={(open) => !open && setShowSchedulerModal(false)}>
        <Dialog.Portal>
          <Dialog.Overlay
            animation="quick"
            opacity={0.6}
            style={{
              zIndex: 1000,
              backgroundColor: 'rgba(0, 0, 0, 0.4)',
              ...(Platform.OS === 'web' && {
                backdropFilter: 'blur(8px)',
              })
            }}
            enterStyle={{ opacity: 0 }}
            exitStyle={{ opacity: 0 }}
          />
          <Dialog.Content
            bordered
            elevate
            animation={[
              'quick',
              {
                opacity: {
                  overshootClamping: true,
                },
              },
            ]}
            enterStyle={{ x: 0, y: -20, opacity: 0, scale: 0.9 }}
            exitStyle={{ x: 0, y: 10, opacity: 0, scale: 0.95 }}
            x={0}
            scale={1}
            style={[
              {
                padding: 0,
                minWidth: Platform.OS === 'web' ? 700 : undefined,
                maxWidth: Platform.OS === 'web' ? 900 : '95%',
                maxHeight: Platform.OS === 'web' ? '90%' : '95%',
                borderRadius: 24,
                overflow: 'hidden',
                elevation: 12,
                shadowColor: '#000',
                shadowOffset: { width: 0, height: 12 },
                shadowOpacity: 0.15,
                shadowRadius: 32,
                backgroundColor: theme ? 'rgba(255, 255, 255, 0.98)' : 'rgba(30, 41, 59, 0.98)',
                borderWidth: 1,
                borderColor: theme ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.1)',
              },
              Platform.OS === 'web' && {
                backdropFilter: 'blur(24px)',
                boxShadow: '0 12px 48px rgba(0,0,0,0.12)',
              }
            ]}
            opacity={1}
            y={0}
          >
            {/* Header */}
            <View style={{
              backgroundColor: theme ? '#f8fafc' : '#374151',
              paddingHorizontal: 24,
              paddingVertical: 20,
              borderBottomWidth: 1,
              borderBottomColor: theme ? '#e5e7eb' : '#4b5563',
            }}>
              <XStack alignItems="center" justifyContent="space-between">
                <XStack alignItems="center" gap="$3">
                  <View style={{
                    backgroundColor: theme ? "#f0f9ff" : "#0c4a6e",
                    borderRadius: 12,
                    padding: 10,
                  }}>
                    <FontAwesome6
                      name="robot"
                      size={20}
                      color={theme ? "#0ea5e9" : "#38bdf8"}
                    />
                  </View>
                  <YStack flex={1}>
                    <Dialog.Title style={{
                      fontSize: 20,
                      fontWeight: '700',
                      color: theme ? '#1e293b' : '#f1f5f9',
                      marginBottom: 4,
                      margin: 0,
                    }}>
                      Schedule Posts with AI
                    </Dialog.Title>
                    <Dialog.Description style={{
                      fontSize: 14,
                      color: theme ? '#64748b' : '#94a3b8',
                      fontWeight: '500',
                      margin: 0,
                    }}>
                      Optimize your posting schedule using AI
                    </Dialog.Description>
                  </YStack>
                </XStack>
                <Pressable
                  onPress={() => setShowSchedulerModal(false)}
                  style={{
                    padding: 8,
                    borderRadius: 8,
                    backgroundColor: theme ? '#F3F4F6' : '#4B5563'
                  }}
                >
                  <AntDesign name="close" size={20} color={theme ? '#1F2937' : '#F9FAFB'} />
                </Pressable>
              </XStack>
            </View>

            {/* Content */}
            <View style={{
              padding: 0,
              backgroundColor: theme ? '#ffffff' : '#1e293b',
              flex: 1,
              maxHeight: Platform.OS === 'web' ? 'calc(90vh - 100px)' : undefined,
            }}>
              <React.Suspense
                fallback={
                  <View style={{
                    flex: 1,
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: 40,
                    minHeight: 200
                  }}>
                    <ActivityIndicator size="large" color={theme ? '#333' : '#fff'} />
                    <Text style={{
                      color: theme ? '#666' : '#999',
                      marginTop: 16,
                      fontSize: 14
                    }}>
                      Loading scheduler...
                    </Text>
                  </View>
                }
              >
                <SocialMediaSchedulerLazy
                  content={messageValue}
                  image={postImage}
                  url={(() => {
                    // Check if there are other integrations selected (besides just the feed)
                    const hasOtherIntegrations = Object.keys(selectedIntegrations).some(
                      key => selectedIntegrations[key] && key !== 'feed'
                    );
                    // If model is selected and other integrations are available, include form URL
                    if (selectedDataForm?._id && hasOtherIntegrations) {
                      const formUrl = `${HOST}/forms/builder?id=${selectedDataForm._id}`;
                      // Use form URL if no other URL is provided, otherwise keep existing URL
                      return postURL || formUrl;
                    }
                    return postURL;
                  })()}
                  selectedIntegrations={Object.keys(selectedIntegrations).filter(key => selectedIntegrations[key])}
                  onContentChange={(newContent) => {
                    setMessageValue(newContent);
                  }}
                  onScheduleComplete={() => {
                    console.log('Posts scheduled successfully');
                    setShowSchedulerModal(false);
                    // Clear the form
                    setMessageValue('');
                    setPostImage({ uri: null });
                    setPostURL(null);
                    setSelectedDataForm(null);
                    // Refetch if available
                    refetch?.();
                  }}
                  onCancel={() => {
                    setShowSchedulerModal(false);
                  }}
                />
              </React.Suspense>
            </View>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog>
    </Animatable.View>
  );
};

export default PostToFeed;
